import React, { useEffect, useMemo, useState } from 'react'
import {
  FiBell,
  FiDownloadCloud,
  FiLock,
  FiMonitor,
  FiNavigation,
  FiSettings,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi'
import { formatNotificationDate } from '../../utils/notificationHelpers'
import { fetchUserSessions, revokeUserSession } from '../../services/userSessionsApi'
import './SettingsPage.css'

const NAV_SECTIONS = [
  {
    id: 'general',
    label: 'General',
    icon: FiSettings,
    title: 'Configuración General',
    subtitle: 'Controla los parámetros operativos por defecto para tu equipo.',
  },
  {
    id: 'notifications',
    label: 'Notificaciones',
    icon: FiBell,
    title: 'Alertas y Notificaciones',
    subtitle: 'Decide qué eventos generan avisos y por qué canal se envían.',
  },
  {
    id: 'privacy',
    label: 'Privacidad y Cookies',
    icon: FiShield,
    title: 'Privacidad y Cookies',
    subtitle: 'Administra tus consentimientos y el uso de datos dentro de Flotte.',
  },
  {
    id: 'security',
    label: 'Seguridad',
    icon: FiLock,
    title: 'Seguridad de la Cuenta',
    subtitle: 'Refuerza accesos y monitorea sesiones activas en segundos.',
  },
]

const TIMEZONES = [
  { value: 'America/Mexico_City', label: '(GMT-06:00) Ciudad de México' },
  { value: 'America/Bogota', label: '(GMT-05:00) Bogotá' },
  { value: 'America/Lima', label: '(GMT-05:00) Lima' },
  { value: 'America/Santiago', label: '(GMT-04:00) Santiago' },
  { value: 'America/Panama', label: '(GMT-05:00) Panamá' },
  { value: 'Europe/Madrid', label: '(GMT+01:00) Madrid' },
]

const NOTIFICATION_GROUPS = [
  {
    title: 'Actividad Operativa',
    description: 'Mantente al tanto de cada movimiento crítico en tus operaciones.',
    items: [
      { id: 'new-requests', label: 'Nuevas solicitudes', defaultPush: true },
      { id: 'status-changes', label: 'Cambios de estado', defaultPush: true },
      { id: 'messages', label: 'Mensajes', defaultPush: true },
    ],
  },
  {
    title: 'Marketing',
    description: 'Controla el tipo de comunicación comercial que quieres recibir.',
    items: [
      { id: 'product-news', label: 'Novedades de producto', defaultPush: false },
      { id: 'promotions', label: 'Promociones', defaultPush: false },
    ],
  },
]

const COOKIE_CONTROLS = [
  {
    id: 'essential',
    label: 'Esenciales',
    description: 'Necesarias para que el sitio funcione.',
    locked: true,
  },
  {
    id: 'analytics',
    label: 'Analíticas',
    description: 'Nos ayudan a mejorar la plataforma (Google Analytics).',
    locked: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Para mostrarte anuncios relevantes.',
    locked: false,
  },
]

export default function SettingsPage({ user }) {
  const [activeSection, setActiveSection] = useState('general')
  const [generalSettings, setGeneralSettings] = useState({
    language: 'es',
    unitSystem: 'metric',
    timezone: TIMEZONES[0].value,
  })
  const [notificationPreferences, setNotificationPreferences] = useState(() => {
    const seed = {}
    NOTIFICATION_GROUPS.forEach(group => {
      group.items.forEach(item => {
        seed[item.id] = { email: true, push: Boolean(item.defaultPush) }
      })
    })
    return seed
  })
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false,
  })
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState(null)
  const [revokingSessions, setRevokingSessions] = useState({})
  const activeSectionMeta = useMemo(
    () => NAV_SECTIONS.find(section => section.id === activeSection) || NAV_SECTIONS[0],
    [activeSection]
  )
  const userId = user?.id

  useEffect(() => {
    if (!userId) {
      setSessions([])
      setSessionsLoading(false)
      setSessionsError(null)
      return
    }

    let cancelled = false

    const loadSessions = async () => {
      setSessionsLoading(true)
      setSessionsError(null)
      try {
        const records = await fetchUserSessions({ userId })
        if (!cancelled) {
          setSessions(records)
        }
      } catch (error) {
        if (!cancelled) {
          setSessionsError(error?.message || 'No se pudieron cargar las sesiones activas.')
        }
      } finally {
        if (!cancelled) {
          setSessionsLoading(false)
        }
      }
    }

    loadSessions()

    return () => {
      cancelled = true
    }
  }, [userId])

  const generalCopy = useMemo(() => ({
    title: 'Parámetros operativos por defecto',
    intro: 'Define cómo se comporta Flotte para tu equipo diario. Estos ajustes impactan formularios, reportes y recordatorios.',
  }), [])

  const renderSection = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="settings-section">
            <SectionHeader title="General" subtitle={generalCopy.intro} />
            <div className="settings-field">
              <label htmlFor="language-select">Idioma</label>
              <select
                id="language-select"
                value={generalSettings.language}
                onChange={event =>
                  setGeneralSettings(prev => ({ ...prev, language: event.target.value }))
                }
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="settings-field">
              <p className="settings-field-label">Unidades de medida</p>
              <div className="unit-toggle" role="group" aria-label="Seleccionar unidades">
                {UNIT_SYSTEMS.map(unit => {
                  const isActive = generalSettings.unitSystem === unit.id
                  return (
                    <button
                      key={unit.id}
                      type="button"
                      className={`unit-toggle__option${isActive ? ' is-active' : ''}`}
                      onClick={() =>
                        setGeneralSettings(prev => ({
                          ...prev,
                          unitSystem: unit.id,
                        }))
                      }
                      aria-pressed={isActive}
                    >
                      {unit.label}
                      <span>{unit.description}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="settings-field">
              <label htmlFor="timezone-select">Zona horaria</label>
              <select
                id="timezone-select"
                value={generalSettings.timezone}
                onChange={event =>
                  setGeneralSettings(prev => ({ ...prev, timezone: event.target.value }))
                }
              >
                {TIMEZONES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )
      case 'notifications':
        return (
          <div className="settings-section">
            <SectionHeader
              title="Notificaciones"
              subtitle="Orquesta cómo y por qué te avisamos. Activa sólo lo necesario para tu operación." />
            <div className="notifications-table">
              <div className="notifications-head">
                <span>Preferencia</span>
                <span>Email</span>
                <span>Push / SMS</span>
              </div>
              {NOTIFICATION_GROUPS.map(group => (
                <div key={group.title} className="notification-group">
                  <div className="notification-group-header">
                    <div>
                      <p>{group.title}</p>
                      <small>{group.description}</small>
                    </div>
                  </div>
                  {group.items.map(item => (
                    <div key={item.id} className="notification-row">
                      <div className="notification-label">
                        <strong>{item.label}</strong>
                      </div>
                      <ToggleSwitch
                        id={`${item.id}-email`}
                        checked={notificationPreferences[item.id]?.email}
                        onChange={() => handleNotificationToggle(item.id, 'email')}
                      />
                      <ToggleSwitch
                        id={`${item.id}-push`}
                        checked={notificationPreferences[item.id]?.push}
                        onChange={() => handleNotificationToggle(item.id, 'push')}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      case 'privacy':
        return (
          <div className="settings-section">
            <SectionHeader title="Gestión de Consentimiento" subtitle="Administra tus permisos de datos y cookies para mantener la operación 100% compliant." />
            <div className="cookie-card">
              {COOKIE_CONTROLS.map(control => (
                <div
                  key={control.id}
                  className={`cookie-row${control.locked ? ' is-locked' : ''}`}
                >
                  <div className="cookie-meta">
                    <div className="cookie-label">
                      {control.locked ? (
                        <span className="cookie-lock-icon" aria-hidden="true">
                          <FiLock size={14} />
                        </span>
                      ) : null}
                      <strong>{control.label}</strong>
                    </div>
                    <p>{control.description}</p>
                  </div>
                  <ToggleSwitch
                    id={`cookie-${control.id}`}
                    checked={cookiePreferences[control.id]}
                    disabled={control.locked}
                    onChange={() =>
                      setCookiePreferences(prev => ({
                        ...prev,
                        [control.id]: control.locked ? true : !prev[control.id],
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <div className="data-rights">
              <p>
                Puedes solicitar una copia de toda tu información operativa exportada en formato seguro con un click.
              </p>
              <button type="button" className="ghost-button">
                <FiDownloadCloud size={16} />
                Descargar mis datos
              </button>
            </div>
          </div>
        )
      case 'security':
        return (
          <div className="settings-section">
            <SectionHeader title="Seguridad" subtitle="Refuerza el acceso a tus datos y monitorea qué dispositivos tienen sesión abierta." />
            <div className="security-card">
              <div className="security-card__icon">
                <FiShield size={20} />
              </div>
              <div className="security-card__body">
                <h3>Autenticación de dos factores</h3>
                <p>
                  Añade un segundo paso al iniciar sesión para bloquear accesos no autorizados.
                </p>
              </div>
              <button
                type="button"
                className="outline-button"
                onClick={() => setTwoFactorEnabled(value => !value)}
              >
                {twoFactorEnabled ? 'Configurado' : 'Activar'}
              </button>
            </div>
            <div className="sessions-card">
              <div className="sessions-header">
                <h4>Sesiones activas</h4>
                <span>Monitorea dónde sigues conectado.</span>
              </div>
              {sessionsError ? (
                <div className="sessions-alert" role="alert">
                  {sessionsError}
                </div>
              ) : null}
              {sessionsLoading ? (
                <div className="sessions-empty">Cargando sesiones en tiempo real...</div>
              ) : sessions.length ? (
                <div className="sessions-list sessions-list--table">
                  {sessions.map(session => {
                    const metaLine = [session.platform, session.browser]
                      .filter(Boolean)
                      .join(' · ')
                    const lastActiveLabel =
                      formatNotificationDate(session.lastActive) || 'Actividad reciente'
                    const isRevoking = Boolean(revokingSessions[session.id])
                    const SessionIcon = resolveSessionIcon(session.device)
                    return (
                      <div key={session.id} className="session-row session-row--table">
                        <div className="session-device">
                          <span className="session-device-icon">
                            <SessionIcon size={16} />
                          </span>
                          <div>
                            <strong>{session.device}</strong>
                            <p>{session.location}</p>
                            {metaLine ? <span className="session-device-meta">{metaLine}</span> : null}
                            {session.ipAddress ? (
                              <span className="session-ip">IP {session.ipAddress}</span>
                            ) : null}
                            {session.isCurrent ? (
                              <span className="session-badge session-badge--inline">
                                <FiLock size={12} /> Sesión actual
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <span className="session-last-active">{lastActiveLabel}</span>
                        <button
                          type="button"
                          className="ghost-button ghost-button-sm ghost-button-sm--danger"
                          disabled={session.isCurrent || isRevoking}
                          onClick={() => handleSessionRevoke(session.id)}
                        >
                          {isRevoking ? 'Cerrando…' : 'Cerrar'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="sessions-empty">
                  <strong>Sin sesiones activas</strong>
                  <p>
                    {userId
                      ? 'No detectamos otras sesiones abiertas en este momento.'
                      : 'Inicia sesión para ver las sesiones asociadas a tu cuenta.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const handleNotificationToggle = (itemId, channel) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [channel]: !prev[itemId]?.[channel],
      },
    }))
  }

  const handleSessionRevoke = async sessionId => {
    if (!sessionId) return
    setSessionsError(null)
    setRevokingSessions(prev => ({ ...prev, [sessionId]: true }))
    try {
      await revokeUserSession(sessionId)
      setSessions(prev => prev.filter(session => session.id !== sessionId))
    } catch (error) {
      setSessionsError(error?.message || 'No se pudo cerrar la sesión seleccionada.')
    } finally {
      setRevokingSessions(prev => {
        const next = { ...prev }
        delete next[sessionId]
        return next
      })
    }
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-shell">
          <nav className="settings-nav" aria-label="Configuración principal">
            {NAV_SECTIONS.map(section => {
              const Icon = section.icon
              const isActive = activeSection === section.id
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`settings-nav-item${isActive ? ' is-active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                  aria-pressed={isActive}
                >
                  <span className="settings-nav-icon">
                    <Icon size={18} />
                  </span>
                  <span className="settings-nav-label">{section.label}</span>
                </button>
              )
            })}
          </nav>
          <section className="settings-panel" aria-live="polite">
            <div className="settings-panel-header">
              <div>
                <p>{activeSectionMeta.label}</p>
                <h2>{activeSectionMeta.title}</h2>
                {activeSectionMeta.subtitle ? <span>{activeSectionMeta.subtitle}</span> : null}
              </div>
            </div>
            {renderSection()}
          </section>
        </div>
      </div>
    </div>
  )
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="section-header">
      <h3>{title}</h3>
      {subtitle ? <p>{subtitle}</p> : null}
      <div className="section-divider" />
    </div>
  )
}

function ToggleSwitch({ id, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      className={`toggle-switch${checked ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
      onClick={() => !disabled && onChange?.()}
    >
      <span className="toggle-track">
        <span className="toggle-handle" />
      </span>
    </button>
  )
}

function resolveSessionIcon(label) {
  const normalized = String(label || '').toLowerCase()
  if (normalized.includes('iphone') || normalized.includes('android') || normalized.includes('phone')) {
    return FiNavigation
  }
  if (normalized.includes('ipad') || normalized.includes('tablet')) {
    return FiTrendingUp
  }
  return FiMonitor
}

const UNIT_SYSTEMS = [
  {
    id: 'metric',
    label: 'Sistema Métrico',
    description: 'kg / km',
  },
  {
    id: 'imperial',
    label: 'Sistema Imperial',
    description: 'lb / mi',
  },
]
